import { useMemo } from 'react';
import { cn } from '@/utils/cn';

export interface PasswordStrengthMeterProps {
  password: string;
  showRequirements?: boolean;
  className?: string;
  /** When true, unmet requirements are highlighted in red with animation */
  validationAttempted?: boolean;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const requirements: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'At least one number', test: (p) => /[0-9]/.test(p) },
  { label: 'At least one special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
];

type StrengthLevel = 'none' | 'weak' | 'fair' | 'good' | 'strong';

interface StrengthConfig {
  label: string;
  color: string;
  bgColor: string;
  textColor: string;
}

const strengthConfigs: Record<StrengthLevel, StrengthConfig> = {
  none: { label: '', color: 'bg-gray-200', bgColor: 'bg-gray-200', textColor: 'text-gray-500' },
  weak: { label: 'Weak', color: 'bg-red-500', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  fair: { label: 'Fair', color: 'bg-yellow-500', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
  good: { label: 'Good', color: 'bg-blue-500', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  strong: { label: 'Strong', color: 'bg-green-500', bgColor: 'bg-green-100', textColor: 'text-green-700' },
};

/**
 * Password Strength Meter Component
 * Displays password requirements and strength indicator
 */
export default function PasswordStrengthMeter({
  password,
  showRequirements = true,
  className,
  validationAttempted = false,
}: PasswordStrengthMeterProps) {
  const { passedCount, passedRequirements, strength } = useMemo(() => {
    const passed = requirements.map((req) => ({
      ...req,
      passed: req.test(password),
    }));
    const count = passed.filter((r) => r.passed).length;

    let level: StrengthLevel = 'none';
    if (password.length === 0) {
      level = 'none';
    } else if (count <= 1) {
      level = 'weak';
    } else if (count <= 2) {
      level = 'fair';
    } else if (count <= 3) {
      level = 'good';
    } else {
      level = 'strong';
    }

    return {
      passedCount: count,
      passedRequirements: passed,
      strength: level,
    };
  }, [password]);

  const config = strengthConfigs[strength];
  const segmentCount = 4;

  if (!password && !showRequirements) {
    return null;
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Strength meter bar */}
      {password && (
        <div className="space-y-1">
          <div className="flex gap-1" role="progressbar" aria-valuenow={passedCount} aria-valuemin={0} aria-valuemax={requirements.length}>
            {Array.from({ length: segmentCount }).map((_, index) => {
              const isActive = strength !== 'none' && index < Math.ceil((passedCount / requirements.length) * segmentCount);
              return (
                <div
                  key={index}
                  className={cn(
                    'h-1.5 flex-1 rounded-full transition-colors duration-200',
                    isActive ? config.color : 'bg-gray-200'
                  )}
                />
              );
            })}
          </div>
          {config.label && (
            <div className="flex justify-between items-center">
              <span className={cn('text-xs font-medium', config.textColor)}>
                Password strength: {config.label}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Requirements checklist */}
      {showRequirements && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-gray-700">Password requirements:</p>
          <ul className="space-y-1" aria-label="Password requirements">
            {passedRequirements.map((req, index) => {
              // Determine styling based on requirement status and validation state
              const isUnmetAfterValidation = !req.passed && validationAttempted;

              return (
                <li
                  key={index}
                  className={cn(
                    'flex items-center text-xs transition-colors duration-150',
                    req.passed
                      ? 'text-green-600'
                      : isUnmetAfterValidation
                        ? 'text-red-600 font-medium animate-pulse-once'
                        : 'text-gray-500'
                  )}
                >
                  {req.passed ? (
                    // Green checkmark for met requirements
                    <svg
                      className="h-3.5 w-3.5 mr-1.5 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : isUnmetAfterValidation ? (
                    // Red X icon for unmet requirements after validation attempt
                    <svg
                      className="h-3.5 w-3.5 mr-1.5 text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  ) : (
                    // Gray circle for unmet requirements before validation attempt
                    <svg
                      className="h-3.5 w-3.5 mr-1.5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="8" strokeWidth={2} />
                    </svg>
                  )}
                  <span className={req.passed ? 'line-through' : ''}>{req.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Validate password against all requirements
 * Returns error message if invalid, null if valid
 */
export function validatePassword(password: string): string | null {
  for (const req of requirements) {
    if (!req.test(password)) {
      return req.label;
    }
  }
  return null;
}
